import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ratingService } from '../services/api';
import useAuthStore from '../store/authStore';
import { Button, Textarea, EmptyState, Spinner } from './ui';
import StarRating, { StarDisplay } from './StarRating';

function timeAgo(dateStr, t) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return t('just now');
  if (diff < 3600) return t('{{count}}m ago', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('{{count}}h ago', { count: Math.floor(diff / 3600) });
  return t('{{count}}d ago', { count: Math.floor(diff / 86400) });
}

export default function ModuleReviews({ moduleId, canReview }) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myScore, setMyScore] = useState(0);
  const [myReview, setMyReview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reportingId, setReportingId] = useState(null);
  const [reportReason, setReportReason] = useState('');

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ratingService.getModuleRatings(moduleId);
      const data = res.data || [];
      const list = Array.isArray(data) ? data : data.results || [];
      setRatings(list);
      const mine = list.find((r) => r.user?.id === user?.id);
      if (mine) {
        setMyScore(mine.score);
        setMyReview(mine.review || '');
      }
    } catch (err) {
      console.warn('Failed to fetch ratings', err);
    } finally {
      setLoading(false);
    }
  }, [moduleId, user]);

  useEffect(() => { fetchRatings(); }, [fetchRatings]);

  const myExistingRating = ratings.find((r) => r.user?.id === user?.id);

  const handleSubmit = async () => {
    if (!myScore) return;
    setSubmitting(true);
    try {
      if (myExistingRating) {
        await ratingService.updateRating(myExistingRating.id, { module: moduleId, score: myScore, review: myReview });
      } else {
        await ratingService.createRating({ module: moduleId, score: myScore, review: myReview });
      }
      await fetchRatings();
    } catch (err) {
      console.error('Failed to submit rating', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async (ratingId) => {
    if (!reportReason.trim()) return;
    try {
      await ratingService.reportRating({ rating: ratingId, reason: reportReason });
      setReportingId(null);
      setReportReason('');
    } catch (err) {
      console.error('Failed to report rating', err);
    }
  };

  const average = ratings.length
    ? ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length
    : 0;
  const distribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: ratings.filter((r) => r.score === star).length,
  }));

  if (loading) {
    return <div className="py-16 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col sm:flex-row gap-8">
          <div className="flex flex-col items-center justify-center sm:border-e sm:pe-8 shrink-0">
            <div className="text-4xl font-bold text-gray-900">{average.toFixed(1)}</div>
            <StarDisplay value={average} size="w-5 h-5" />
            <div className="text-sm text-gray-500 mt-1">
              {t('{{count}} reviews', { count: ratings.length })}
            </div>
          </div>
          <div className="flex-1 space-y-1.5 min-w-0">
            {distribution.map(({ star, count }) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="w-3 text-gray-500">{star}</span>
                <svg className="w-3.5 h-3.5 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.5l2.9 6.26 6.9.79-5.13 4.78 1.4 6.83L12 17.6l-6.07 3.56 1.4-6.83L2.2 9.55l6.9-.79L12 2.5z" />
                </svg>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full"
                    style={{ width: ratings.length ? `${(count / ratings.length) * 100}%` : '0%' }}
                  />
                </div>
                <span className="w-6 text-gray-400 text-xs text-end">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {canReview && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            {myExistingRating ? t('Edit your review') : t('Leave a review')}
          </h3>
          <StarRating value={myScore} onChange={setMyScore} />
          <Textarea
            className="mt-3"
            rows={3}
            placeholder={t('Share your experience with this course...')}
            value={myReview}
            onChange={(e) => setMyReview(e.target.value)}
          />
          <Button className="mt-3" onClick={handleSubmit} loading={submitting} disabled={!myScore}>
            {myExistingRating ? t('Update Review') : t('Submit Review')}
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {ratings.length === 0 ? (
          <EmptyState
            title={t('No reviews yet')}
            description={t('Be the first to share your experience with this course.')}
          />
        ) : (
          ratings.map((r) => (
            <div key={r.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-sm shrink-0">
                    {(r.user?.first_name || '?')[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {r.user?.first_name} {r.user?.last_name}
                    </div>
                    <div className="flex items-center gap-2">
                      <StarDisplay value={r.score} size="w-3.5 h-3.5" />
                      <span className="text-xs text-gray-400">{timeAgo(r.created_at, t)}</span>
                    </div>
                  </div>
                </div>
                {r.user?.id !== user?.id && (
                  <button
                    onClick={() => setReportingId(reportingId === r.id ? null : r.id)}
                    className="text-xs text-gray-400 hover:text-red-500 shrink-0"
                  >
                    {t('Report')}
                  </button>
                )}
              </div>
              {r.review && <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{r.review}</p>}

              {reportingId === r.id && (
                <div className="mt-3 pt-3 border-t flex gap-2">
                  <input
                    type="text"
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5"
                    placeholder={t('Why are you reporting this review?')}
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                  />
                  <Button size="sm" variant="danger" onClick={() => handleReport(r.id)} disabled={!reportReason.trim()}>
                    {t('Submit')}
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
